import { Future, Startable, TypeUtilities } from '@hds/common-utils';
import EntityChangeFeedWatcher from './EntityChangeFeedWatcher.mjs';

class EntityChangeFeed extends Startable {
    #_EntityKlass;
    #_changeEventsPipelineManager;
    #_watcherRegistry = new Map(); //Map <watchId, EntityChangeFeedWatcher>
    //Inverse Map for efficient updates/removes
    #_watchedEntityIdsRegistry = new Map(); //Map <entityId, Map<watchId,*EntityChangeFeedWatcher>>
    //used to synchronize the initiated operation and the receipt of the corresponding change in this changefeed
    //Operations:
    //      create
    //      upsert
    //      update
    //      replace
    //      patch
    #_pendingOperations = new Map(); //< operationId, pendingOperationDetails
    //used to synchronize the initiated operation (identified by EntityId) and the receipt of the corresponding change in this changefeed
    //Operations:
    //      appendTo
    //      clone
    //      delete
    #_pendingEntityIdIdentifiedOperations = new Map(); //< entityId, Array.<pendingOperationDetails >
    #_failureListeners = new Map();
    #_loadAndSynchronizeBuffers = new Map();
    #_projection = undefined;
    constructor(EntityKlass, changeEventsPipelineManager, projection = undefined) {
        super();
        this.#_EntityKlass = EntityKlass;
        this.#_changeEventsPipelineManager = changeEventsPipelineManager;
        this.#_projection = projection;
    }
    /*****************************************************/
    /* START ABSTRACT METHODS */
    /*****************************************************/
    async _createEntityPersistence() {
        throw new Error('Subclass must implement _createEntityPersistence()!');
    }
    /*****************************************************/
    /* END ABSTRACT METHODS */
    /* START STARTABLE OVERRIDDEN METHODS */
    /*****************************************************/
    async _restart() {
        await this.#_changeEventsPipelineManager.restart();
        this.#_changeEventsPipelineManager.addFailureListener(this.#_triggerFailure.bind(this));
        //resync watchers
        const watchersBeingLoadAndSynchronized = [];
        const allWatchers = this.getAllWatchers();
        for (let [watcherId, watcher] of Object.entries(allWatchers)) {
            watchersBeingLoadAndSynchronized.push(this.#_loadAndSynchronize(watcher));
        }
        await Promise.all(watchersBeingLoadAndSynchronized);
    }
    async _start() {
        await this.#_changeEventsPipelineManager.whenStarted(this._changeEventsProcessorFn = this._changeEventsProcessorFn.bind(this));
        this.#_changeEventsPipelineManager.addFailureListener(this.#_triggerFailure.bind(this));
    }
    async _shutdown() {
        await this.#_changeEventsPipelineManager.whenShutdown();
        this.removeAllFailureListeners();
        const allWatchers = this.getAllWatchers();
        for (let [watcherId, watcher] of Object.entries(allWatchers)) {
            watcher.removeAllListeners();
        }
        this.#_watcherRegistry.clear();
        this.#_watcherRegistry = null;
    }
    /*****************************************************/
    /* END STARTABLE OVERRIDDEN METHODS */
    /* START ENTITY FACTORY METHODS */
    /*****************************************************/
    async createEntityInstance(document, noSync = false) {
        const ep = await this._createEntityPersistence();
        const entity = new this.EntityKlass(ep, document, { autoSynchronization: false });
        if (noSync === false) {
            entity._synchronize();
        }
        return entity;
    }
    /*****************************************************/
    /* END ENTITY FACTORY METHODS */
    /* START OPERATION METHODS */
    /*****************************************************/
    async createEntity(document) {
        const future = new Future();
        const operationId = crypto.randomUUID();
        document.__lastOperationId = operationId;
        const entity = await this.createEntityInstance(document, true);
        try {
            this.#_pendingOperations.set(operationId, {
                operation: 'create',
                operationId,
                document,
                future
            });
            await entity.create();
        }
        catch (e) {
            this.#_pendingOperations.delete(operationId);
            future.reject(e);
        }
        return future.promise;
    }
    async upsertEntity(document) {
        const future = new Future();
        const operationId = crypto.randomUUID();
        document.__lastOperationId = operationId;
        const entity = await this.createEntityInstance(document, true);
        try {
            this.#_pendingOperations.set(operationId, {
                operation: 'upsert',
                operationId,
                document,
                future
            });
            await entity.upsert();
        }
        catch (e) {
            this.#_pendingOperations.delete(operationId);
            future.reject(e);
        }
        return future.promise;
    }
    async updateEntity(document) {
        const future = new Future();
        const operationId = crypto.randomUUID();
        document.__lastOperationId = operationId;
        const entity = await this.createEntityInstance(document, true);
        try {
            this.#_pendingOperations.set(operationId, {
                operation: 'update',
                operationId,
                document,
                future
            });
            await entity.update();
        }
        catch (e) {
            this.#_pendingOperations.delete(operationId);
            future.reject(e);
        }
        return future.promise;
    }
    async replaceEntity(document, entityIdToReplace = document?.id) {
        const future = new Future();
        const operationId = crypto.randomUUID();
        document.__lastOperationId = operationId;
        const entity = await this.createEntityInstance(document, true);
        try {
            this.#_pendingOperations.set(operationId, {
                operation: 'replace',
                operationId,
                document,
                future
            });
            await entity.replace(entityIdToReplace);
        }
        catch (e) {
            this.#_pendingOperations.delete(operationId);
            future.reject(e);
        }
        return future.promise;
    }
    async patchEntity(id, jsonPatch) {
        const future = new Future();
        const operationId = crypto.randomUUID();
        jsonPatch.push({
            op: 'add',
            path: '/__lastOperationId',
            value: operationId
        });
        const document = { id };
        const entity = await this.createEntityInstance(document, true);
        try {
            this.#_pendingOperations.set(operationId, {
                operation: 'patch',
                operationId,
                document,
                future
            });
            await entity.patchAndSave(jsonPatch);
        }
        catch (e) {
            this.#_pendingOperations.delete(operationId);
            future.reject(e);
        }
        return future.promise;
    }
    //START EntityId identified operations
    async appendToEntity(entityId, pathToArray, arrayElement) {
        const future = new Future();
        const entity = await this.createEntityInstance({ id: entityId }, true);
        try {
            const entitySpecificPendingOperations = this.#_pendingEntityIdIdentifiedOperations.get(entityId) || [];
            entitySpecificPendingOperations.push({
                operation: 'appendTo',
                entityId,
                future
            });
            this.#_pendingEntityIdIdentifiedOperations.set(entityId, entitySpecificPendingOperations);
            await entity.appendAndSave(pathToArray, arrayElement);
        }
        catch (e) {
            const entitySpecificPendingOperations = this.#_pendingEntityIdIdentifiedOperations.get(entityId) || [];
            const foundIndex = entitySpecificPendingOperations.findIndex((elem) => {
                return elem.future === future;
            });
            if (foundIndex !== -1) {
                delete entitySpecificPendingOperations[foundIndex];
            }
            future.reject(e);
        }
        return future.promise;
    }
    async cloneEntity(idToClone, newId) {
        if (!TypeUtilities.isOfTypeString(newId)) {
            throw new Error('ID_TO_CLONE_IS_INVALID');
        }
        const future = new Future();
        const entity = await this.createEntityInstance({ id: idToClone }, true);
        try {
            const entitySpecificPendingOperations = this.#_pendingEntityIdIdentifiedOperations.get(newId) || [];
            entitySpecificPendingOperations.push({
                operation: 'clone',
                idToClone,
                newId,
                future
            });
            this.#_pendingEntityIdIdentifiedOperations.set(newId, entitySpecificPendingOperations);
            await entity.clone(newId);
        }
        catch (e) {
            const entitySpecificPendingOperations = this.#_pendingEntityIdIdentifiedOperations.get(newId) || [];
            const foundIndex = entitySpecificPendingOperations.findIndex((elem) => {
                return elem.future === future;
            });
            if (foundIndex !== -1) {
                delete entitySpecificPendingOperations[foundIndex];
            }
            future.reject(e);
        }
        return future.promise;
    }
    //Any deletes with this id will match.
    async deleteEntity(entityIdToDelete) {
        const future = new Future();
        const entity = await this.createEntityInstance({ id: entityIdToDelete }, true);
        try {
            const entitySpecificPendingOperations = this.#_pendingEntityIdIdentifiedOperations.get(entityIdToDelete) || [];
            entitySpecificPendingOperations.push({
                operation: 'delete',
                entityIdToDelete,
                future
            });
            this.#_pendingEntityIdIdentifiedOperations.set(entityIdToDelete, entitySpecificPendingOperations);
            await entity.delete();
        }
        catch (e) {
            const entitySpecificPendingOperations = this.#_pendingEntityIdIdentifiedOperations.get(entityIdToDelete) || [];
            const foundIndex = entitySpecificPendingOperations.findIndex((elem) => {
                return elem.future === future;
            });
            if (foundIndex !== -1) {
                delete entitySpecificPendingOperations[foundIndex];
            }
            future.reject(e);
        }
        return future.promise;
    }
    /*****************************************************/
    /* END OPERATION METHODS */
    /* START ENTITY RETRIEVAL METHODS */
    /*****************************************************/
    async retrieveEntity(id, entityOptions = { autoSynchronization: true, projection: undefined }) {
        let noSync = false;
        if (entityOptions?.autoSynchronization) {
            noSync = !entityOptions.autoSynchronization;
        }
        const entity = await this.createEntityInstance({ id }, noSync);
        await entity.retrieve(entityOptions.projection);
        return entity;
    }
    async #_createEntitiesMapFromEntityDataModelArray(entityDataModelArray, entityOptions) {
        let noSync = false;
        if (entityOptions?.autoSynchronization) {
            noSync = !entityOptions.autoSynchronization;
        }
        const ep = await this._createEntityPersistence();

        const retrievedMap = new Map();
        for (const edm of entityDataModelArray) {
            const entity = await this.createEntityInstance(edm.toJSON(), noSync);
            entity._synchronize();
            retrievedMap.set(edm.id, entity);
        }
        return retrievedMap;
    }
    async retrieveEntitiesByIds(entityIds, entityOptions = { autoSynchronization: true, projection: undefined }) {
        const ep = await this._createEntityPersistence();
        const retrieveByIdsResults = await ep.retrieveByIds(entityIds, entityOptions.projection);
        return await this.#_createEntitiesMapFromEntityDataModelArray(retrieveByIdsResults.retrievedEntities, entityOptions);
    }
    async retrieveEntitiesByProperties(propertiesFilter, entityOptions = { autoSynchronization: true, projection: undefined }) {
        const ep = await this._createEntityPersistence();
        const retrieveByPropertiesResults = await ep.retrieveByProperties(propertiesFilter, entityOptions.projection);
        return await this.#_createEntitiesMapFromEntityDataModelArray(retrieveByPropertiesResults.retrievedEntities, entityOptions);
    }
    async retrieveAllEntities(entityOptions = { autoSynchronization: true, projection: undefined }) {
        const ep = await this._createEntityPersistence();
        const retrievedResults = await ep.retrieveAll(entityOptions.projection);
        return await this.#_createEntitiesMapFromEntityDataModelArray(retrievedResults.retrievedEntities, entityOptions);
    }
    /*****************************************************/
    /* END ENTITY RETRIEVAL METHODS */
    /* START CHANGE STREAM HANDLING */
    /*****************************************************/
    addFailureListener(callback, thisContext) {
        const changeListenerId = crypto.randomUUID();
        this.#_failureListeners.set(changeListenerId, { callback, thisContext });
        return changeListenerId;
    }
    removeFailureListener(changeListenerOrListenerId) {
        let foundChangeListenerId = changeListenerOrListenerId;
        if (TypeUtilities.isOfTypeFunction(changeListenerOrListenerId)) {
            for (const [changeListenerId, callbackDetails] of this.#_failureListeners) {
                if (callbackDetails.callback === changeListenerOrListenerId) {
                    foundChangeListenerId = changeListenerId;
                }
            }
        }
        if (foundChangeListenerId) {
            return this.#_failureListeners.delete(foundChangeListenerId);
        }
    }
    removeAllFailureListeners() {
        this.#_failureListeners.clear();
    }
    #_triggerFailure(failureError) {
        for (const [changeListenerId, callbackDetails] of this.#_failureListeners) {
            callbackDetails.callback.call(callbackDetails.thisContext, failureError);
        }
    }
    //When changes to entities occur
    async _changeEventsProcessorFn(changeEvent) {
        try {
            //update the load and synchronize buffers
            for (const [changeEventsBufferId, buffer] of this.#_loadAndSynchronizeBuffers.entries()) {
                buffer.push(changeEvent);
            }

            let watchers;
            const watcherProms = [];
            const pendingOperationDetailsByLastOperationId = this.#_pendingOperations.get(changeEvent?.document?.__lastOperationId);
            const pendingOperationDetailsByEntityId = this.#_pendingEntityIdIdentifiedOperations.get(changeEvent.id);
            switch (changeEvent.type) {
                case 'create':
                    //Notify the watchers of the create
                    watchers = this.getInterestedWatchers(changeEvent.document);
                    for (const [watcherId, watcher] of Object.entries(watchers)) {
                        const createdEntity = await this.createEntityInstance(changeEvent.document);
                        if (!watcher.hasEntity(changeEvent.id)) {
                            //ensure entity & watcher are added to the watchedEntityIdsRegistry
                            this.#_addWatcherToWatchedEntityRegistry(changeEvent.id, watcher);
                            watcherProms.push(watcher.addEntity(createdEntity, changeEvent));
                        }
                    }
                    const createdEntityForPendingOperation = await this.createEntityInstance(changeEvent.document)
                    //Resolve pending create operations
                    if (pendingOperationDetailsByLastOperationId) {
                        pendingOperationDetailsByLastOperationId.future.resolve(createdEntityForPendingOperation);
                        this.#_pendingOperations.delete(changeEvent.document.__lastOperationId);
                    }
                    //resolve any pending clone operations
                    else if (pendingOperationDetailsByEntityId) {
                        const foundIndex = pendingOperationDetailsByEntityId.findIndex((elem) => {
                            return elem.operation === 'clone';
                        });
                        if (foundIndex !== -1) {
                            const pendingOperationDetails = pendingOperationDetailsByEntityId[foundIndex];
                            pendingOperationDetails.future.resolve(createdEntityForPendingOperation);
                            pendingOperationDetailsByEntityId.splice(foundIndex, 1);
                        }
                    }
                    break;
                case 'update':
                    //Notify the watchers of the create
                    watchers = this.getAllWatchers();//Note: Can't use .getInterestedWatchers() for the scenario when an update makes a watcher no longer interested
                    for (const [watcherId, watcher] of Object.entries(watchers)) {
                        if (this.isWatcherInterested(changeEvent.document, watcher) === true) {
                            const updatedEntity = await this.createEntityInstance(changeEvent.document);
                            //When the update makes the entity now match the property filter
                            if (!watcher.hasEntity(changeEvent.id)) {
                                //ensure entity & watcher are added to the watchedEntityIdsRegistry
                                this.#_addWatcherToWatchedEntityRegistry(changeEvent.id, watcher);
                                watcherProms.push(watcher.addEntity(updatedEntity, changeEvent));
                            }
                            else {
                                watcherProms.push(watcher.updateEntity(changeEvent.id, changeEvent.document, changeEvent));
                            }
                        }
                        else {
                            //if this watcher was interested prior to the update
                            if (watcher.hasEntity(changeEvent.id)) {
                                //ensure entity & watcher are removed from the watchedEntityIdsRegistry
                                this.#_removeWatcherFromWatchedEntityRegistry(changeEvent.id, watcher);
                                if (watcher.hasEntity(changeEvent.id)) {
                                    watcherProms.push(watcher.removeEntityById(changeEvent.id, changeEvent));
                                }
                            }
                        }
                    }
                    const updatedEntityForPendingOperation = await this.createEntityInstance(changeEvent.document);
                    //Resolve pending update operations
                    if (pendingOperationDetailsByLastOperationId) {
                        pendingOperationDetailsByLastOperationId.future.resolve(updatedEntityForPendingOperation);
                        this.#_pendingOperations.delete(changeEvent.document.__lastOperationId);
                    }
                    //resolve any pending appendTo operations
                    else if (pendingOperationDetailsByEntityId) {
                        const foundIndex = pendingOperationDetailsByEntityId.findIndex((elem) => {
                            return elem.operation === 'appendTo';
                        });
                        if (foundIndex !== -1) {
                            const pendingOperationDetails = pendingOperationDetailsByEntityId[foundIndex];
                            pendingOperationDetails.future.resolve(updatedEntityForPendingOperation);
                            pendingOperationDetailsByEntityId.splice(foundIndex, 1);
                        }
                    }
                    break;
                case 'delete':
                    watchers = this.getAllWatchers();//Can't use .getInterestedWatchers() as we don't have the whole document in a delete changeEvent
                    for (const [watcherId, watcher] of Object.entries(watchers)) {
                        //ensure entity & watcher are removed from the watchedEntityIdsRegistry
                        this.#_removeWatcherFromWatchedEntityRegistry(changeEvent.id, watcher);
                        if (watcher.hasEntity(changeEvent.id)) {
                            watcherProms.push(watcher.removeEntityById(changeEvent.id, changeEvent));
                        }
                    }
                    //Resolve pending delete operations
                    if (pendingOperationDetailsByEntityId) {
                        const foundIndex = pendingOperationDetailsByEntityId.findIndex((elem) => {
                            return elem.operation === 'delete';
                        });
                        if (foundIndex !== -1) {
                            const pendingOperationDetails = pendingOperationDetailsByEntityId[foundIndex];
                            pendingOperationDetails.future.resolve(changeEvent.id);
                            pendingOperationDetailsByEntityId.splice(foundIndex, 1);

                        }
                    }
                    break;
            }
            await Promise.all(watcherProms);
        }
        catch (changeEventsProcessingError) {
            this.#_triggerFailure(changeEventsProcessingError);
        }
    }
    /*****************************************************/
    /* END CHANGE STREAM HANDLING */
    /* START WATCHER RETRIEVAL METHODS */
    /*****************************************************/
    getAllWatchers() {
        return Object.fromEntries(this.#_watcherRegistry);
    }
    getWatcher(watchId) {
        return this.#_watcherRegistry.get(watchId);
    }
    isWatcherInterested(document, watcher) {
        let isInterested = false;
        switch (watcher.watchType) {
            case `by-id`:
                if (document.id === watcher.watchFilter.id) {
                    isInterested = true;
                }
                break;
            case `by-ids`:
                if (watcher.watchFilter.ids.includes(document.id)) {
                    isInterested = true;
                }
                break;
            case `by-properties`:
                let matchFound = true;
                for (let [propName, propVal] of Object.entries(watcher.watchFilter.propertiesFilter)) {
                    if (document[propName] !== propVal) {
                        matchFound = false;
                    }
                }
                if (matchFound === true) {
                    isInterested = true;
                }
                break;
            case `by-all`:
                isInterested = true;
                break;
        }
        return isInterested;
    }
    getInterestedWatchers(document) {
        const interestedWatchersObj = {};
        for (let [registeredWatcherId, registeredWatcher] of this.#_watcherRegistry) {
            if (this.isWatcherInterested(document, registeredWatcher) === true) {
                interestedWatchersObj[registeredWatcherId] = registeredWatcher;
            }
        }
        return interestedWatchersObj;
    }
    getWatchersByLoadedEntityId(entityId) {
        const interestedWatchersMap = this.#_watchedEntityIdsRegistry.get(entityId);
        if (interestedWatchersMap) {
            return Object.fromEntries(interestedWatchersMap);
        }
        else {
            return {};
        }
    }
    getWatchersByLoadedEntityIds(entityIds) {
        const interestedWatchersObj = {};
        for (const entityId of entityIds) {
            const interestedInEntityIdWatchersMap = this.#_watchedEntityIdsRegistry.get(entityId);
            if (interestedInEntityIdWatchersMap) {
                interestedWatchersObj[entityId] = Object.fromEntries(interestedInEntityIdWatchersMap);
            }
        }
        return interestedWatchersObj;
    }
    /*****************************************************/
    /* END START WATCHER RETRIEVAL METHODS */
    /* START LOAD AND SYNCHRONIZE METHODS */
    /*****************************************************/
    #_startBufferingChangeEvents() {
        const bufferId = crypto.randomUUID();
        this.#_loadAndSynchronizeBuffers.set(bufferId, []);
        return bufferId;
    }
    #_stopBufferingChangeEvents(changeEventsBufferId) {
        this.#_loadAndSynchronizeBuffers.delete(changeEventsBufferId);
    }
    async #_loadAndSynchronize(watcher) {
        const changeEventsBufferId = this.#_startBufferingChangeEvents();
        const bufferedChangeEvents = this.#_loadAndSynchronizeBuffers.get(changeEventsBufferId);//this is all the change events encountered during the load phase

        //Based on the watcher type retrieve the existing entities this watcher is interested in
        switch (watcher.watchType) {
            case `by-id`:
                let entity = await this.createEntityInstance({ id: watcher.watchFilter.id }, true);
                try {
                    await entity.retrieve(this.#_projection);
                    await watcher.addEntity(entity);
                }
                catch (e) {
                    if (e.message !== 'ID_NOT_FOUND') {
                        throw e;
                    }
                }
                break;
            case `by-ids`:
                const retrievedByIds = await this.retrieveEntitiesByIds(watcher.watchFilter.ids, { autoSynchronization: false, projection: this.#_projection });
                await watcher.addEntities(retrievedByIds.values());
                break;
            case `by-properties`:
                const retrievedByProperties = await this.retrieveEntitiesByProperties(watcher.watchFilter.propertiesFilter, { autoSynchronization: false, projection: this.#_projection });
                await watcher.addEntities(retrievedByProperties.values());
                break;
            case `by-all`:
                const retrieveByAll = await this.retrieveAllEntities({ autoSynchronization: false, projection: this.#_projection });
                await watcher.addEntities(retrieveByAll.values());
                break;
        }
        this.#_stopBufferingChangeEvents(changeEventsBufferId);

        //process any buffered changes
        for (const changeEvent of bufferedChangeEvents) {
            switch (changeEvent.type) {
                case 'create':
                    if (this.isWatcherInterested(changeEvent.document, watcher) === true) {
                        //if something was created during retrieval, make sure to add it
                        if (!watcher.hasEntity(changeEvent.id)) {
                            let entity = await this.createEntityInstance(changeEvent.document);
                            await watcher.addEntity(entity, changeEvent);
                        }
                    }
                    break;
                case 'update':
                    if (this.isWatcherInterested(changeEvent.document, watcher) === true) {
                        //When the update makes the entity now match the property filter
                        if (!watcher.hasEntity(changeEvent.id)) {
                            let entity = await this.createEntityInstance(changeEvent.document);
                            await watcher.addEntity(entity, changeEvent);
                        }
                        else {
                            await watcher.updateEntity(changeEvent.id, changeEvent.document, changeEvent);
                        }
                    }
                    else {
                        //if this watcher was interested prior to the update
                        if (watcher.hasEntity(changeEvent.id)) {
                            await watcher.removeEntity(watcher.getEntity(changeEvent.id, changeEvent));
                        }
                    }
                    break;
                case 'delete':
                    if (watcher.hasEntity(changeEvent.id)) {
                        //if something was deleted during retrieval, make sure it was deleted
                        await watcher.removeEntity(watcher.getEntity(changeEvent.id), changeEvent);
                    }
                    break;
            }
        }

        for (const [entityId, entity] of watcher.entities) {
            //ensure entity & watcher are added to the watchedEntityIdsRegistry
            this.#_addWatcherToWatchedEntityRegistry(entityId, watcher);
        }
        //add watcher to the WatcherRegistry
        this.#_watcherRegistry.set(watcher.watchId, watcher);
    }
    //ensure entity & watcher are added to the watchedEntityIdsRegistry
    #_addWatcherToWatchedEntityRegistry(entityId, watcher) {
        if (this.#_watchedEntityIdsRegistry.has(entityId) === false) {
            this.#_watchedEntityIdsRegistry.set(entityId, new Map());
        }
        const watchedEntityIdReg = this.#_watchedEntityIdsRegistry.get(entityId);
        watchedEntityIdReg.set(watcher.watchId, watcher);
    }
    //ensure entity & watcher are removed from the watchedEntityIdsRegistry
    #_removeWatcherFromWatchedEntityRegistry(entityId, watcher) {
        if (this.#_watchedEntityIdsRegistry.has(entityId) === true) {
            const watchedEntityIdReg = this.#_watchedEntityIdsRegistry.get(entityId);
            if (watchedEntityIdReg.has(watcher.watchId)) {
                watchedEntityIdReg.delete(watcher.watchId);
            }
            if (watchedEntityIdReg.size === 0) {
                this.#_watchedEntityIdsRegistry.delete(entityId);
            }
        }
    }
    //remove the watcher from all entities in the _watchedEntityIdsRegistry
    #_removeWatcherFromWatchedEntityRegistryForAllEntities(watcher) {
        for (const [loadedEntityId, interestedWatchers] of this.#_watchedEntityIdsRegistry) {
            if (interestedWatchers.has(watcher.watchId)) {
                interestedWatchers.delete(watcher.watchId);
            }
            if (interestedWatchers.size === 0) {
                this.#_watchedEntityIdsRegistry.delete(loadedEntityId);
            }
        }
    }
    /*****************************************************/
    /* END LOAD AND SYNCHRONIZE METHODS */
    /* START WATCH/UNWATCH METHODS */
    /*****************************************************/
    #_validateEntityId(entityId) {
        if (!TypeUtilities.isOfTypeString(entityId)) {
            throw new Error('INVALID_ID');
        }
    }
    unwatch(watchId) {
        const entityChangeFeedWatcher = this.#_watcherRegistry.get(watchId);
        if (entityChangeFeedWatcher) {
            entityChangeFeedWatcher.removeAllListeners();
            this.#_watcherRegistry.delete(watchId);
            this.#_removeWatcherFromWatchedEntityRegistryForAllEntities(entityChangeFeedWatcher);
            return true;
        }
        else {
            return false;
        }
    }
    //Start watching on a specific ID
    async watch(entityId) {
        this.#_validateEntityId(entityId);
        const entityChangeFeedWatcher = new EntityChangeFeedWatcher(
            crypto.randomUUID(),
            {
                type: 'by-id',
                id: entityId
            }
        );
        await this.#_loadAndSynchronize(entityChangeFeedWatcher);
        return entityChangeFeedWatcher;
    }
    async watchByIds(entityIds) {
        if (!TypeUtilities.isOfTypeArray(entityIds)) {
            throw new Error('INVALID_ID');
        }
        for (const entityId of entityIds) {
            this.#_validateEntityId(entityId);
        }
        const entityChangeFeedWatcher = new EntityChangeFeedWatcher(
            crypto.randomUUID(),
            {
                type: 'by-ids',
                ids: entityIds
            }
        );
        await this.#_loadAndSynchronize(entityChangeFeedWatcher);
        return entityChangeFeedWatcher;
    }
    async watchByProperties(propertiesFilter) {
        const entityChangeFeedWatcher = new EntityChangeFeedWatcher(
            crypto.randomUUID(),
            {
                type: 'by-properties',
                propertiesFilter: propertiesFilter
            }
        );
        await this.#_loadAndSynchronize(entityChangeFeedWatcher);
        return entityChangeFeedWatcher;
    }
    async watchAll() {
        const entityChangeFeedWatcher = new EntityChangeFeedWatcher(
            crypto.randomUUID(),
            {
                type: 'by-all'
            }
        );
        await this.#_loadAndSynchronize(entityChangeFeedWatcher);
        return entityChangeFeedWatcher;
    }
    /*****************************************************/
    /* END WATCH/UNWATCH METHODS */
    /*****************************************************/
    get EntityKlass() {
        return this.#_EntityKlass;
    }
    get changeEventsPipelineManager() {
        return this.#_changeEventsPipelineManager;
    }
}
export { EntityChangeFeed };
export default EntityChangeFeed;
